import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Timer, Eye, ChartLine, Lock } from "@phosphor-icons/react";
import { usePublicExamCategories, categoryVisual } from "../lib/examCategories";

export default function Landing() {
  const { items: publicCategories, loading: catsLoading } = usePublicExamCategories();
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Top bar */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-l border-zinc-200 pl-3 ml-1">
              Examination Platform v1.0
            </span>
          </div>
          <nav className="flex items-center gap-2">
            <Link to="/pricing" className="hidden sm:inline-block text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="nav-pricing">Pricing</Link>
            <Link to="/login" className="text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="nav-login">Sign in</Link>
            <Link to="/register" className="text-sm font-medium px-4 py-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors" data-testid="nav-register">
              Get started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-200 grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6 font-semibold">
              Enterprise Examination Infrastructure
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              The exam platform built for serious institutions.
            </h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-xl leading-relaxed">
              Run secure online assessments at scale. Question banks, proctored exams,
              instant evaluation, deep analytics—engineered for universities, certification
              bodies, and enterprise training teams.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
                data-testid="hero-register-btn"
              >
                Start free
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 border border-zinc-300 px-6 py-3.5 text-sm font-medium hover:bg-zinc-50 transition-colors"
                data-testid="hero-login-btn"
              >
                Sign in
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-green-600" />
                WhatsApp demo on request
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-green-600" />
                No credit card
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-zinc-900 bg-white">
              <div className="border-b border-zinc-200 px-5 py-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Live Exam Session</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 pulse-dot" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-700 font-semibold">In progress</span>
                </div>
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Question 04 / 12</div>
                <div className="font-display text-xl font-semibold leading-tight">
                  Which data structure provides O(1) average lookup time?
                </div>
                <div className="mt-5 space-y-2.5">
                  {["Linked List", "Hash Table", "Binary Tree", "Sorted Array"].map((o, i) => (
                    <div key={i} className={`border px-4 py-3 text-sm flex items-center gap-3 ${i === 1 ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-6">{String.fromCharCode(65 + i)}</span>
                      {o}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-zinc-200 pt-4">
                  <div className="font-mono-tab text-lg font-bold">14:23</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Time remaining</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-4 gap-0 border border-zinc-200">
            {[
              { icon: Timer, title: "Timed Exams", desc: "Auto-submit, grace periods, time extensions, and per-section timing." },
              { icon: Eye, title: "Live Proctoring", desc: "Real-time monitoring dashboard with attempt status and submission tracking." },
              { icon: ChartLine, title: "Deep Analytics", desc: "Subject-wise breakdown, rank, percentile, and class-wide performance." },
              { icon: Lock, title: "Secure by Default", desc: "Role-based access, session cookies, and answer integrity guarantees." },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className={`p-8 ${i < 3 ? "border-r border-zinc-200" : ""} ${i % 4 < 4 ? "border-b lg:border-b-0 border-zinc-200" : ""}`}>
                <Icon size={28} weight="regular" className="text-zinc-900 mb-6" />
                <div className="font-display text-xl font-bold mb-2">{title}</div>
                <div className="text-sm text-zinc-600">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse by category */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="landing-categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2 font-semibold">Browse by category</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Find an exam by sector</h2>
              <p className="text-sm text-zinc-600 mt-2 max-w-xl">
                Jump straight into the exam catalog for banking, IT, general knowledge and more. No sign-in needed to browse.
              </p>
            </div>
            <Link
              to="/browse"
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-4 py-2 hover:border-zinc-900"
              data-testid="landing-view-all-categories"
            >
              View all exams <ArrowRight size={13} weight="bold" />
            </Link>
          </div>

          {catsLoading ? (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="landing-categories-loading">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-28 border border-zinc-200 bg-white animate-pulse" />
              ))}
            </div>
          ) : publicCategories.length === 0 ? (
            <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 bg-white" data-testid="landing-categories-empty">
              No published exams yet. Institution admins can publish exams to make them appear here.
            </div>
          ) : (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="landing-categories-grid">
              {publicCategories.map((c) => {
                const { Icon, tone } = categoryVisual(c.slug);
                return (
                  <Link
                    key={c.slug}
                    to={`/browse/${c.slug}`}
                    className={`group relative border ${tone.ring} ${tone.bg} p-5 flex flex-col transition-all hover:-translate-y-0.5 hover:border-zinc-900`}
                    data-testid={`landing-category-tile-${c.slug}`}
                  >
                    <div className={`w-11 h-11 flex items-center justify-center ${tone.iconBg} mb-4`}>
                      <Icon size={22} weight="regular" className={tone.iconFg} />
                    </div>
                    <div className="font-display text-base sm:text-lg font-bold leading-tight" data-testid={`landing-category-label-${c.slug}`}>
                      {c.label}
                    </div>
                    <div className="mt-1 text-xs text-zinc-600 flex items-center gap-1">
                      <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${tone.chip}`} data-testid={`landing-category-count-${c.slug}`}>
                        {c.count} exam{c.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="mt-auto pt-4 text-[11px] uppercase tracking-wider font-semibold text-zinc-900 inline-flex items-center gap-1 opacity-60 group-hover:opacity-100">
                      Explore <ArrowRight size={11} weight="bold" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="md:hidden mt-6">
            <Link
              to="/browse"
              className="inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-4 py-2 hover:border-zinc-900"
              data-testid="landing-view-all-categories-mobile"
            >
              View all exams <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Request demo */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Get started</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">Request a demo</h2>
          <p className="text-sm text-zinc-600 max-w-xl mb-8">Talk to our team and get a walkthrough of every role — Super Admin, Institution Admin, Examiner, Proctor and Student.</p>
          <div className="border border-zinc-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Contact</div>
              <div className="font-display text-2xl font-bold tracking-tight" data-testid="landing-contact-number">+91 91546 37357</div>
              <div className="text-xs text-zinc-500 mt-1">Reach us on WhatsApp — replies within business hours.</div>
            </div>
            <a
              href="https://wa.me/919154637357?text=Request%20for%20demo"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:bg-[#20BA5A] transition-colors"
              data-testid="landing-request-demo-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
              </svg>
              Request Demo on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-xs text-zinc-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>© {new Date().getFullYear()} ExamOS. Built for institutional rigor.</span>
            <span className="text-zinc-300">·</span>
            <Link
              to="/privacy"
              className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <span className="text-zinc-300">·</span>
            <Link
              to="/terms"
              className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-mono">v1.0</span>
            <span className="text-zinc-300">·</span>
            <span>
              developed by{" "}
              <a
                href="https://vvtechnocrats.com/"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-zinc-700 hover:text-zinc-900 underline underline-offset-2"
                data-testid="footer-developer-link"
              >
                @v3vidyavikas
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
